/**
 * Every state says it in words (proposal 10, kanban f81b3383).
 *
 * Overdue turned red and answered turned green with no words, so a reader
 * who cannot tell red from green could not tell them apart either. These
 * helpers give each state its sentence; the colour only repeats it. Red is
 * kept for the two states that need a person to act now: overdue and not
 * delivered.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';

/** bad = red (overdue, not delivered), warn = amber (waiting), unread =
 *  yellow (answered, not read yet), good = green (answered, read). */
export type StateTone = 'bad' | 'warn' | 'unread' | 'good';

export interface StateWord {
  text: string;
  tone: StateTone;
}

const DAY_MS = 86_400_000;

/** A yyyy-mm-dd date is a local day; anything else is an instant. */
function parse(value: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
}

function dayStart(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Whole calendar days from `from` to `to` (local), never negative. */
export function calendarDays(from: string, to: Date): number {
  const start = parse(from);
  if (Number.isNaN(start.getTime())) return 0;
  return Math.max(0, Math.round((dayStart(to) - dayStart(start)) / DAY_MS));
}

/** "1 day" / "3 days". */
export function daysWord(n: number): string {
  return n === 1 ? '1 day' : `${n} days`;
}

/** "Mon" when the day falls within the coming week, else "Mon, Oct 19". */
export function dueDay(value: string, now: Date): string {
  const d = parse(value);
  const ahead = Math.round((dayStart(d) - dayStart(now)) / DAY_MS);
  return d.toLocaleDateString(
    'en-US',
    ahead >= 0 && ahead < 7
      ? { weekday: 'short' }
      : { weekday: 'short', month: 'short', day: 'numeric' },
  );
}

/** Past its day: the due day is before today (due today is not late yet). */
export function isPastDue(due: string | null | undefined, now: Date): boolean {
  if (!due) return false;
  const d = parse(due);
  return !Number.isNaN(d.getTime()) && dayStart(now) > dayStart(d);
}

/**
 * One wait on a person: "⏰ Overdue 2 days · chase" once its date has
 * passed, else "⏳ waiting 1 day · reminder Mon" (or "· until Fri" for a
 * wait the reader set by hand).
 */
export function waitWord(
  wait: { since: string | null; due: string | null; dueWord?: 'reminder' | 'until' },
  now: Date = new Date(),
): StateWord {
  if (wait.due && isPastDue(wait.due, now)) {
    return { text: `⏰ Overdue ${daysWord(calendarDays(wait.due, now))} · chase`, tone: 'bad' };
  }
  const days = wait.since ? calendarDays(wait.since, now) : null;
  const how =
    days === null ? 'waiting' : days === 0 ? 'waiting since today' : `waiting ${daysWord(days)}`;
  const when = wait.due ? ` · ${wait.dueWord ?? 'reminder'} ${dueDay(wait.due, now)}` : '';
  return { text: `⏳ ${how}${when}`, tone: 'warn' };
}

/** "✓ Answered · unread" until the reader has seen it, then "✓ Answered". */
export function answeredWord(unread: boolean): StateWord {
  return unread
    ? { text: '✓ Answered · unread', tone: 'unread' }
    : { text: '✓ Answered', tone: 'good' };
}

type AskFields = Pick<
  DoorDataRequestRead,
  'status' | 'answer_unseen' | 'created_at' | 'next_followup_at'
> &
  Partial<Pick<DoorDataRequestRead, 'sent_at'>>;

/**
 * The state of the asks on one item or case: an open ask waits (since the
 * oldest one went out, until the soonest reminder); with none open, an
 * answered one says Answered. Null when nothing was asked.
 */
export function askWord(asks: AskFields[], now: Date = new Date()): StateWord | null {
  // A pass-along (kanban 196e9d9b) is not waited on: it never sets the word.
  const open = asks.filter((r) => r.status === 'open' && (r as { kind?: string }).kind !== 'pass_along');
  if (open.length > 0) {
    const sent = open.map((r) => r.sent_at ?? r.created_at).sort()[0];
    const reminders = open.map((r) => r.next_followup_at).filter((d): d is string => !!d);
    const due = reminders.sort()[0] ?? null;
    return waitWord({ since: sent, due, dueWord: 'reminder' }, now);
  }
  const answered = asks.filter((r) => r.status === 'answered');
  if (answered.length === 0) return null;
  return answeredWord(answered.some((r) => r.answer_unseen));
}

/** The posture pill: rising and falling say so with an arrow, not a colour. */
export function postureWords(state: string, label: string): string {
  if (state === 'escalating') return `▲ ${label}`;
  if (state === 'de_escalated') return `▼ ${label}`;
  return label;
}

/** The Done tab's grey badge: notes added to closed items (not an alarm). */
export function notesAfterClosingWords(n: number, compact = false): string {
  const notes = n === 1 ? '1 note' : `${n} notes`;
  return compact ? notes : `${notes} added after closing`;
}

/** The Triage tab's amber badge: items that moved since the reader looked. */
export function changedSinceWords(n: number, compact = false): string {
  return compact ? `${n} changed` : `${n} changed since you looked`;
}

/** The word on anything that never reached the person. */
export const NOT_DELIVERED_WORDS = '✗ Not delivered';
